import React, { useContext, useMemo } from 'react'
import AreaChart from '../../../components/charts/AreaChart'
import { StatsCard } from '../../../components/charts/StatsCard'
import { formatCurrency } from '../../../lib/utils/formatNumber'
import {
  DollarSign,
  Euro,
  PoundSterling,
  Calendar,
  TriangleAlert,
  FolderClosed,
} from 'lucide-react'
import { match } from 'ts-pattern'
import MultiLineChart from '../../../components/charts/MultilineChart'
import { Button } from '../../../components/ui/Button'
import { User } from '../../../lib/types/User'
import { useGetStockMetrics } from '../../../api/reports/useGetStockMetrics'
import { DateRangeContext } from '../../../context/DateRangeProvider'
import { Skeleton } from '../../../components/skeleton/Skeleton'

export const getCurrencyIcon = (code?: string): React.ReactNode => {
  return match(code)
    .with('usd', () => (<DollarSign />) as React.ReactNode)
    .with('eur', () => (<Euro />) as React.ReactNode)
    .with('gbp', () => (<PoundSterling />) as React.ReactNode)
    .otherwise(() => null)
}

const Stocks = ({
  user,
  currencies,
}: {
  user?: User
  currencies?: Array<{
    id: number
    name: string
    code: string
    symbol: string
  }>
}): JSX.Element => {
  const { dateRange } = useContext(DateRangeContext)
  const { data: stockData, isLoading } = useGetStockMetrics({
    dateRange,
  })

  const totalTargetDividendAmount = useMemo(
    () => stockData?.reduce((acc, cur) => acc + cur?.amount_target, 0),
    [stockData],
  )

  const groupedData = useMemo(() => {
    if (!stockData?.length) return []
    const uniqueCurrencies = [
      ...new Set(stockData.map((item) => item.local_currency_code)),
    ]

    const baseStructure = Object.fromEntries(
      uniqueCurrencies.map((currency) => [currency, 0]),
    )
    return Object.values(
      stockData.reduce((acc, { date, amount_local, local_currency_code }) => {
        if (!acc[date]) {
          acc[date] = {
            date,
            ...baseStructure,
          }
        }
        acc[date][local_currency_code] += amount_local
        return acc
      }, {} as Record<string, any>),
    )
  }, [stockData])
  const datasets = useMemo(
    () =>
      Object.keys(
        groupedData.reduce((acc, item) => {
          Object.keys(item).forEach((key) => {
            if (key !== 'date') acc[key] = true
          })
          return acc
        }, {}),
      ).map((code, i) => ({
        yKey: code,
        label: currencies?.find((c) => c.code === code)?.name,
        colorScheme: i === 0 ? ('emerald-glow' as const) : undefined,
      })),
    [groupedData, currencies],
  )

  const missingRateData = useMemo(() => {
    return stockData
      ?.filter((d) => d.amount_target === null)
      ?.reduce((acc: Array<{ currencies: string; dates: Set<string> }>, cur) => {
        const existingCurrency = acc.findIndex(
          (d) =>
            d.currencies === `${cur.local_currency_code}/${cur.target_currency_code}`,
        )

        if (existingCurrency === -1) {
          const dates = match(cur.gain_strategy)
            .with(1, () => [cur.date])
            .with(2, () => {
              const result = []
              if (!cur.sale_rate) result.push(cur.date)
              if (!cur.purchase_rate) result.push(cur.purchase_date)
              return result
            })
            .otherwise(() => [cur.date])

          acc.push({
            currencies: `${cur.local_currency_code}/${cur.target_currency_code}`,
            dates: new Set(dates),
          })
        } else {
          acc[existingCurrency].dates.add(cur.date)
        }

        return acc
      }, [] as Array<{ currencies: string; dates: Set<string> }>)
  }, [stockData])

  const cumulativeTransactions = useMemo(
    () =>
      Object.values(
        stockData?.reduce((acc, { date, amount_local, amount_target }) => {
          if (!acc[date]) acc[date] = { date }
          acc[date].amount_local = (acc[date].amount_local || 0) + amount_local
          acc[date].amount_target = (acc[date].amount_target || 0) + amount_target
          return acc
        }, {} as Record<string, any>) ?? {},
      )?.reduce((acc, cur, i) => {
        if (i === 0) return [cur]
        const total = acc[i - 1].amount_target
        acc.push({
          ...cur,
          amount_target: total + cur.amount_target,
        })
        return acc
      }, [] as Array<{ date: string; amount_target: number }>),
    [stockData],
  )
  return (
    <div className="w-full  flex flex-col gap-5 py-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatsCard
          isLoading={isLoading}
          title={'Total Uploads'}
          value={new Set(stockData?.map((d) => d.upload_id)).size || 0}
          icon={<FolderClosed />}
        />
        <StatsCard
          isLoading={isLoading}
          title={'Total Sales'}
          value={stockData?.length || 0}
          icon={<Calendar />}
        />
        <StatsCard
          isLoading={isLoading}
          title={`Total amount in target currency (${user?.target_currency_code.toUpperCase()})`}
          value={formatCurrency(totalTargetDividendAmount, user?.target_currency_code)}
          icon={getCurrencyIcon(user?.target_currency_code)}
          className="col-span-2 sm:col-span-1 border-2 border-lime-600 bg-lime-500 dark:bg-green-800"
        />
      </div>
      {missingRateData && Boolean(missingRateData?.length) && (
        <div className="flex flex-col gap-1 p-2 border-2 rounded-lg border-destructive/80 bg-destructive/20 text-destructive font-bold text-sm">
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row items-center gap-4">
              <TriangleAlert />
              <div className="flex flex-col gap-1">
                <span>Missing exchange rate data for the following days</span>
                {missingRateData.map((d) => {
                  return (
                    <span key={d.currencies}>
                      {d.currencies.toUpperCase()}:{' '}
                      {Array.from(d.dates.values()).join(', ')}
                    </span>
                  )
                })}
              </div>
            </div>
            <Button
              variant={'secondary'}
              onClick={(e) => {
                window.open(
                  `mailto:mycapgains@gmail.com?subject=[CapGains] Missing Exchange Rate Data&body=${missingRateData?.map(
                    (d) =>
                      `${d.currencies.toUpperCase()}: ${Array.from(d.dates.values())
                        ?.map((d) => `${d}`)
                        .join(', ')}%0d%0a`,
                  )}`,
                  '_blank',
                )
              }}>
              Report
            </Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isLoading ? (
          Array(2).fill(<Skeleton className="h-[300px] w-full rounded-lg" />)
        ) : (
          <>
            <div
              className={'min-h-[270px] border-2 border-foreground shadow-lg rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-foreground text-lg'
                }>
                <span>Cumulative Gains ({user?.target_currency_code.toUpperCase()})</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <AreaChart
                  data={cumulativeTransactions ?? []}
                  yKey={'amount_target'}
                  gradient={'emerald-glow'}
                />
              </div>
            </div>
            <div
              className={'min-h-[270px] border-2 border-foreground shadow-lg rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-foreground text-lg'
                }>
                <span>Uploaded Stock Transactions</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <MultiLineChart
                  data={groupedData ?? []}
                  datasets={datasets ?? []}
                  customOptions={{
                    elements: {
                      line: {
                        tension: 0,
                      },
                      point: {
                        radius: 0,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Stocks
